import React, { useEffect, useState } from 'react';
import './ReferralsTab.css';
import { API } from '../api';
import { formatCount, formatHoursValue, formatMoney } from '../utils/adminFormatters';
import { formatDate } from '../utils/dateUtils';
import Pagination from './Pagination';

const ReferralsTab = ({ onUserClick }) => {
  const [overview, setOverview] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');
  
  // Pagination
  const [referralsPagination, setReferralsPagination] = useState(null);
  const [codesPagination, setCodesPagination] = useState(null);
  const [referralsPage, setReferralsPage] = useState(1);
  const [codesPage, setCodesPage] = useState(1);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadOverview();
  }, []);

  useEffect(() => {
    if (activeView === 'referrals') {
      loadReferrals();
    }
  }, [activeView, referralsPage, statusFilter]);

  useEffect(() => {
    if (activeView === 'codes') {
      loadCodes();
    }
  }, [activeView, codesPage]);

  const loadOverview = async () => {
    setLoading(true);
    const result = await API.referrals.getOverview();
    if (result.success) {
      setOverview(result.data);
    }
    setLoading(false);
  };

  const loadReferrals = async () => {
    setLoading(true);
    const params = { page: referralsPage, limit: 20 };
    if (statusFilter) params.status = statusFilter;
    
    const result = await API.referrals.getAll(params);
    if (result.success) {
      setReferrals(result.data.data);
      setReferralsPagination(result.data.pagination);
    }
    setLoading(false);
  };

  const loadCodes = async () => {
    setLoading(true);
    const result = await API.referrals.getCodes({ page: codesPage, limit: 20 });
    if (result.success) {
      setCodes(result.data.data);
      setCodesPagination(result.data.pagination);
    }
    setLoading(false);
  };

  const handleGrantReferral = async (referralId) => {
    if (!window.confirm('Вы уверены, что хотите начислить награду за этот реферал?')) return;
    
    const result = await API.referrals.grant(referralId);
    if (result.success) {
      loadReferrals();
      loadOverview();
    } else {
      alert('Ошибка при начислении награды: ' + result.error);
    }
  };

  const handleCancelReferral = async (referralId) => {
    const reason = window.prompt('Укажите причину отмены:');
    if (!reason) return;
    
    const result = await API.referrals.cancel(referralId, reason);
    if (result.success) {
      loadReferrals();
      loadOverview();
    } else {
      alert('Ошибка при отмене реферала: ' + result.error);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Ожидает', className: 'status-pending' },
      granted: { label: 'Начислено', className: 'status-granted' },
      canceled: { label: 'Отменено', className: 'status-canceled' }
    };
    const config = statusMap[status] || statusMap.pending;
    return <span className={`status-badge ${config.className}`}>{config.label}</span>;
  };

  if (loading && !overview) {
    return (
      <div className="referrals-tab">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Загрузка статистики рефералов...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="referrals-tab">
      {/* View Tabs */}
      <div className="referrals-view-tabs">
        <button 
          className={`view-tab ${activeView === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveView('overview')}
        >
          Обзор
        </button>
        <button 
          className={`view-tab ${activeView === 'referrals' ? 'active' : ''}`}
          onClick={() => setActiveView('referrals')}
        >
          Рефералы
        </button>
        <button 
          className={`view-tab ${activeView === 'codes' ? 'active' : ''}`}
          onClick={() => setActiveView('codes')}
        >
          Коды
        </button>
      </div>

      {/* Overview */}
      {activeView === 'overview' && overview && (
        <>
          {/* Stats Grid */}
          <div className="referrals-stats-grid">
            <div className="stat-card">
              <div className="stat-header">
                <h3>Реферальные коды</h3>
              </div>
              <div className="stat-value">{formatCount(overview.total?.referralCodes)}</div>
              <div className="stat-meta">Активных: {formatCount(overview.total?.activeCodes)}</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <h3>Всего рефералов</h3>
              </div>
              <div className="stat-value">{formatCount(overview.total?.totalReferrals)}</div>
              <div className="stat-meta">
                Начислено: {formatCount(overview.total?.grantedReferrals)} · 
                Ожидает: {formatCount(overview.total?.pendingReferrals)}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <h3>Начислено часов</h3>
              </div>
              <div className="stat-value">{formatHoursValue(overview.rewards?.totalHoursGranted)}</div>
              <div className="stat-meta">
                Среднее: {formatHoursValue(overview.rewards?.averageHoursPerReferral)} ч/реферал
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <h3>Рост за 7 дней</h3>
              </div>
              <div className="stat-value">+{formatCount(overview.growth?.referralsLast7Days)}</div>
              <div className="stat-meta">
                За 30 дней: +{formatCount(overview.growth?.referralsLast30Days)}
              </div>
            </div>
          </div>

          {/* Top Referrers */}
          {overview.topReferrers && overview.topReferrers.length > 0 && (
            <div className="top-referrers-section">
              <h2>Топ рефереров</h2>
              <div className="top-referrers-list">
                {overview.topReferrers.map((referrer, index) => (
                  <div 
                    key={referrer.userId} 
                    className="referrer-card"
                    onClick={() => onUserClick && onUserClick(referrer.userId)}
                  >
                    <div className="referrer-rank">#{index + 1}</div>
                    <div className="referrer-info">
                      <div className="referrer-name">
                        {referrer.firstName} {referrer.lastName}
                      </div>
                      <div className="referrer-username">@{referrer.username}</div>
                    </div>
                    <div className="referrer-stats">
                      <div className="referrer-stat">
                        <span className="stat-label">Рефералов</span>
                        <span className="stat-number">{formatCount(referrer.totalReferrals)}</span>
                      </div>
                      <div className="referrer-stat">
                        <span className="stat-label">Успешных</span>
                        <span className="stat-number">{formatCount(referrer.successfulReferrals)}</span>
                      </div>
                      <div className="referrer-stat">
                        <span className="stat-label">Часов</span>
                        <span className="stat-number">{formatHoursValue(referrer.hoursEarned)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Referrals List */}
      {activeView === 'referrals' && (
        <>
          {/* Filters */}
          <div className="referrals-filters">
            <select 
              value={statusFilter} 
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setReferralsPage(1);
              }}
              className="status-filter"
            >
              <option value="">Все статусы</option>
              <option value="pending">Ожидает</option>
              <option value="granted">Начислено</option>
              <option value="canceled">Отменено</option>
            </select>
          </div>

          {/* Table */}
          <div className="referrals-table-container">
            <table className="referrals-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Реферер</th>
                  <th>Реферал</th>
                  <th>Код</th>
                  <th>Статус</th>
                  <th>Награда</th>
                  <th>Дата</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((referral) => (
                  <tr key={referral.id}>
                    <td>#{referral.id}</td>
                    <td>
                      <div 
                        className="user-link"
                        onClick={() => onUserClick && onUserClick(referral.referrer.id)}
                      >
                        {referral.referrer.firstName} {referral.referrer.lastName}
                      </div>
                      <div className="user-subtext">@{referral.referrer.username}</div>
                    </td>
                    <td>
                      <div 
                        className="user-link"
                        onClick={() => onUserClick && onUserClick(referral.referee.id)}
                      >
                        {referral.referee.firstName} {referral.referee.lastName}
                      </div>
                      <div className="user-subtext">@{referral.referee.username}</div>
                    </td>
                    <td><code className="referral-code">{referral.code}</code></td>
                    <td>{getStatusBadge(referral.status)}</td>
                    <td>{formatHoursValue(referral.rewardHours)} ч</td>
                    <td>{formatDate(referral.createdAt)}</td>
                    <td>
                      {referral.status === 'pending' && (
                        <div className="action-buttons">
                          <button 
                            className="action-btn grant"
                            onClick={() => handleGrantReferral(referral.id)}
                            title="Начислить награду"
                          >
                            ✓
                          </button>
                          <button 
                            className="action-btn cancel"
                            onClick={() => handleCancelReferral(referral.id)}
                            title="Отменить"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {referralsPagination && referralsPagination.totalPages > 1 && (
            <Pagination
              currentPage={referralsPagination.page}
              totalPages={referralsPagination.totalPages}
              onPageChange={setReferralsPage}
            />
          )}
        </>
      )}

      {/* Codes List */}
      {activeView === 'codes' && (
        <div className="codes-table-container">
          <table className="referrals-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Код</th>
                <th>Пользователь</th>
                <th>Статус</th>
                <th>Рефералов</th>
                <th>Успешных</th>
                <th>Создан</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((code) => (
                <tr key={code.id}>
                  <td>#{code.id}</td>
                  <td><code className="referral-code">{code.code}</code></td>
                  <td>
                    <div 
                      className="user-link"
                      onClick={() => onUserClick && onUserClick(code.user.id)}
                    >
                      {code.user.firstName} {code.user.lastName}
                    </div>
                    <div className="user-subtext">@{code.user.username}</div>
                  </td>
                  <td>
                    <span className={`status-badge ${code.active ? 'status-active' : 'status-inactive'}`}>
                      {code.active ? 'Активен' : 'Неактивен'}
                    </span>
                  </td>
                  <td>{formatCount(code.totalReferrals)}</td>
                  <td>{formatCount(code.successfulReferrals)}</td>
                  <td>{formatDate(code.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {codesPagination && codesPagination.totalPages > 1 && (
            <Pagination
              currentPage={codesPagination.page}
              totalPages={codesPagination.totalPages}
              onPageChange={setCodesPage}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ReferralsTab;
